import deepEqual from 'fast-deep-equal'
import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import type { AudioNote } from '@highlight/common'
import { getMeetingCalendarEventId } from '@shared/utils/audio-notes/AudioNoteUtils'
import { audioNoteStateAtom } from './use-audio-transcript'

/**
 * Per-field selectors over the 'audio-note-state' GlobalState object, which
 * broadcasts frequently while recording with fresh object refs each time.
 * These narrow the subscription to one field with value equality, so a
 * status/summary tick doesn't re-render consumers of an unchanged field.
 */

const statusAtom = selectAtom(audioNoteStateAtom, (s) => s?.status)
const isTakingNotesAtom = selectAtom(
  audioNoteStateAtom,
  (s) => s?.status === 'recording' || s?.status === 'starting' || s?.status === 'reconnecting',
)
const isRecordingAtom = selectAtom(
  audioNoteStateAtom,
  (s) => s?.status === 'recording' || s?.status === 'paused' || s?.status === 'reconnecting',
)
// The union of the two above plus `saving`: a note that exists and hasn't
// finished saving yet. What the floating chrome keys its recording controls
// off — one predicate so the pill can't blink out of one host and into another
// as the status crosses `starting`, and holds through the save so the pill can
// show its saving state instead of vanishing on Stop (Figma 8476:17270).
const hasLiveNoteAtom = selectAtom(
  audioNoteStateAtom,
  (s) =>
    s?.status === 'recording' ||
    s?.status === 'starting' ||
    s?.status === 'paused' ||
    s?.status === 'reconnecting' ||
    s?.status === 'saving',
)
const isStartingAtom = selectAtom(audioNoteStateAtom, (s) => s?.status === 'starting')
const isPausedAtom = selectAtom(audioNoteStateAtom, (s) => s?.status === 'paused')
const isSavingAtom = selectAtom(audioNoteStateAtom, (s) => s?.status === 'saving')
const isReconnectingAtom = selectAtom(audioNoteStateAtom, (s) => s?.status === 'reconnecting')
// Mirrors useAudioNoteState: an undefined replica counts as active because its
// status !== 'idle'.
const isAudioNoteActiveAtom = selectAtom(audioNoteStateAtom, (s) => s?.status !== 'idle' || !!s?.savedNote)
const activeNoteIdAtom = selectAtom(audioNoteStateAtom, (s) => s?.activeNoteId ?? null)
const audioSessionIdAtom = selectAtom(audioNoteStateAtom, (s) => s?.audioSessionId ?? null)
const chatConversationIdAtom = selectAtom(audioNoteStateAtom, (s) => s?.chatConversationId ?? null)
const startTimeMsAtom = selectAtom(audioNoteStateAtom, (s) => (s?.startTime ? new Date(s.startTime).getTime() : null))
const awaitingJoinMeetingIdAtom = selectAtom(audioNoteStateAtom, (s) => s?.awaitingMeetingJoin?.meeting.id ?? null)
const meetingCalendarEventIdAtom = selectAtom(audioNoteStateAtom, (s) => getMeetingCalendarEventId(s?.meeting) ?? null)
const meetingAtom = selectAtom(audioNoteStateAtom, (s) => s?.meeting ?? null, deepEqual)
// Whether the live note is tied to a detected meeting (Zoom/Meet/Teams/Huddle) —
// labels say "meeting note" only then, "audio note" otherwise.
const hasMeetingAtom = selectAtom(audioNoteStateAtom, (s) => !!s?.meeting)
const detectedMeetingAtom = selectAtom(audioNoteStateAtom, (s) => s?.detectedMeeting ?? null, deepEqual)
const activeNoteTitleAtom = selectAtom(audioNoteStateAtom, (s) => s?.activeNoteTitle ?? null)

// Summary generation. The text itself no longer rides this state — it streams
// over the snapshot feed in `use-streaming-summary` — so what is left here are
// booleans and an error that each flip once per note.
const isSummaryPendingAtom = selectAtom(audioNoteStateAtom, (s) => s?.summaryPending ?? false)
const summaryErrorAtom = selectAtom(audioNoteStateAtom, (s) => s?.summaryError ?? null)
const savedNoteIdAtom = selectAtom(audioNoteStateAtom, (s) => s?.savedNote?.id ?? null)
const summaryNoteIdAtom = selectAtom(audioNoteStateAtom, (s) => s?.summaryNoteId ?? null)
const savedNoteConfirmationAtom = selectAtom(
  audioNoteStateAtom,
  (s) => s?.savedNoteConfirmation ?? null,
  (a, b) => a?.noteId === b?.noteId && a?.savedAt === b?.savedAt,
)

// savedNote arrives as a fresh object on every broadcast (IPC serialization) and
// deepEqual would walk the whole note, so this compares an explicit field list.
//
// Deliberately *not* compared: `shareLink`, `endedAt`, and transcript bodies
// (stripped from the wire). Subscribers needing entries should read through
// `useAudioNoteById` — this atom is the live singleton used as a metadata
// fallback while that query is in flight.
const sameSavedNote = (a: AudioNote | null, b: AudioNote | null) =>
  a?.id === b?.id && a?.title === b?.title && a?.summary === b?.summary
const savedNoteAtom = selectAtom(audioNoteStateAtom, (s) => s?.savedNote ?? null, sameSavedNote)

export const useAudioNoteStatus = () => useAtomValue(statusAtom)
export const useIsTakingNotes = () => useAtomValue(isTakingNotesAtom)
export const useIsRecordingAudioNote = () => useAtomValue(isRecordingAtom)
export const useHasLiveAudioNote = () => useAtomValue(hasLiveNoteAtom)
export const useIsAudioNoteStarting = () => useAtomValue(isStartingAtom)
export const useIsAudioNotePaused = () => useAtomValue(isPausedAtom)
export const useIsAudioNoteSaving = () => useAtomValue(isSavingAtom)
export const useIsAudioNoteReconnecting = () => useAtomValue(isReconnectingAtom)
export const useIsAudioNoteActive = () => useAtomValue(isAudioNoteActiveAtom)
export const useAudioNoteActiveNoteId = () => useAtomValue(activeNoteIdAtom)
export const useAudioNoteAudioSessionId = () => useAtomValue(audioSessionIdAtom)
export const useAudioNoteChatConversationId = () => useAtomValue(chatConversationIdAtom)
export const useAudioNoteStartTimeMs = () => useAtomValue(startTimeMsAtom)
export const useAwaitingJoinMeetingId = () => useAtomValue(awaitingJoinMeetingIdAtom)
export const useAudioNoteMeetingCalendarEventId = () => useAtomValue(meetingCalendarEventIdAtom)
export const useAudioNoteMeeting = () => useAtomValue(meetingAtom)
export const useAudioNoteHasMeeting = () => useAtomValue(hasMeetingAtom)
export const useDetectedAudioNoteMeeting = () => useAtomValue(detectedMeetingAtom)
export const useActiveAudioNoteTitle = () => useAtomValue(activeNoteTitleAtom)
export const useAudioNoteSavedNote = () => useAtomValue(savedNoteAtom)
export const useSavedAudioNoteId = () => useAtomValue(savedNoteIdAtom)
/** The note main's summary state belongs to — outlives `savedNote` being cleared. */
export const useSummaryNoteId = () => useAtomValue(summaryNoteIdAtom)
export const useIsSummaryPending = () => useAtomValue(isSummaryPendingAtom)
export const useAudioNoteSummaryError = () => useAtomValue(summaryErrorAtom)
/** The note the backend last acknowledged saving; renderer-owned summary generation starts from it. */
export const useSavedNoteConfirmation = () => useAtomValue(savedNoteConfirmationAtom)
